import { useCallback, useEffect } from 'react';
import { cloneDeep } from 'lodash-es';
import { useStore } from 'zustand';
import type {
  EnvironmentKey,
  DomainEntity,
  ConfigEntity,
  EntityDetail,
  EntityRootData,
  ConfigEntityMeta,
} from '@web-config-app/core';
import { isConfigEntity } from '@web-config-app/core';
import { API_JSON_CONTENT_TYPE } from '@web-config-app/api';
import {
  useEntityValidate,
  UseEntityValidateOptions,
} from '../use-entity-validate/use-entity-validate.hook';
import {
  UseEntityGetOptions,
  useEntityGet,
} from '../use-entity-get/use-entity-get';
import {
  UseEntityUpdateOptions,
  useEntityUpdate,
} from '../use-entity-update/use-entity-update';
import {
  UseEntityCreateOptions,
  useEntityCreate,
} from '../use-entity-create/use-entity-create';
import { useEntityClone } from '../use-entity-clone/use-entity-clone';
import { UseEntityCustomOperationOptions } from '../use-entity-custom-operation/use-entity-custom-operation';
import { UseEntityDeepCloneOptions } from '../use-entity-deep-clone/use-entity-deep-clone';
import { type BulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';

export interface UseEntityOperationsProps {
  entity: DomainEntity | ConfigEntity;
  // TODO:: Fix inconsistent typing of instanceId since it can be undefined
  // https://everlong.atlassian.net/browse/CACT-1600
  instanceId: string;
  entityRootData: EntityRootData | undefined;
  onEntityGetSuccess?: (
    data: EntityDetail,
    included: EntityDetail[] | undefined,
    meta: ConfigEntityMeta | undefined,
  ) => void;
  isUsingConfigAppBackend?: boolean;
  options?: {
    get?: UseEntityGetOptions;
    update?: UseEntityUpdateOptions;
    create?: UseEntityCreateOptions;
    clone?: UseEntityCreateOptions;
    deepClone?: UseEntityDeepCloneOptions;
    validate?: UseEntityValidateOptions;
    custom?: UseEntityCustomOperationOptions;
  };
  environment: EnvironmentKey;
  /**
   * Optional external bulk action reporting store. When provided, the hook
   * uses this store instead of creating its own. This allows callers (e.g.
   * the config-app page) to share the same store instance with mutation
   * callbacks that drive the reporting modal.
   */
  bulkActionReportingStore: BulkActionReportingStore;
}

export type UseEntityOperationsResult = ReturnType<typeof useEntityOperations>;

/**
 * Provides a convenience composition of all of an entity instance's data operations
 * like GET, UPDATE, DELETE, CREATE and returns them in a single object.
 *
 */

export const useEntityOperations = ({
  entity,
  isUsingConfigAppBackend,
  instanceId,
  entityRootData,
  onEntityGetSuccess,
  options,
  environment,
  bulkActionReportingStore,
}: UseEntityOperationsProps) => {
  const bulkActionReportingSession = useStore(
    bulkActionReportingStore,
    (state) => state.session,
  );

  const clearBulkActionReportingSession = useCallback(() => {
    bulkActionReportingStore.getState().clearSession();
  }, [bulkActionReportingStore]);

  const custom = {
    options: options?.custom,
  };

  // GET Operations — query hooks now take just DomainEntity. Unwrap the ConfigEntity wrap
  // here (mutation hooks below still accept the wrap for envelope construction).
  const domainEntityForQueries = isConfigEntity(entity)
    ? entity.domainEntity
    : entity;
  const get = useEntityGet({
    entity: domainEntityForQueries,
    instanceId,
    environment,
    options: {
      getIncludedEntities: true,
      isUsingConfigAppBackend,
      ...options?.get,
    },
  });

  const deepClone = { options: options?.deepClone };

  // POST Operations
  const create = useEntityCreate({
    entity,
    environment,
    data: entityRootData,
    options: options?.create,
    headers: {
      'Content-Type': API_JSON_CONTENT_TYPE,
    },
    operation: 'create',
  });

  const clone = useEntityClone({
    entity,
    environment,
    data: entityRootData,
    options: options?.clone,
    isUsingConfigAppBackend,
  });

  const validate = useEntityValidate({
    entity,
    environment,
    data: entityRootData,
    options: options?.validate,
  });

  // PATCH Operations
  const update = useEntityUpdate({
    entity,
    environment,
    instanceId,
    data: entityRootData,
    options: options?.update,
    operation: 'update',
  });

  const publish = useEntityUpdate({
    entity,
    environment,
    instanceId,
    data: entityRootData,
    options: options?.update,
    operation: 'publish',
  });

  const republish = useEntityUpdate({
    entity,
    environment,
    instanceId,
    data: entityRootData,
    options: options?.update,
    operation: 'republish',
  });

  const unpublish = useEntityUpdate({
    entity,
    environment,
    instanceId,
    data: entityRootData,
    options: options?.update,
    operation: 'unpublish',
  });

  const archive = useEntityUpdate({
    entity,
    environment,
    instanceId,
    data: entityRootData,
    options: options?.update,
    operation: 'archive',
  });

  const unarchive = useEntityUpdate({
    entity,
    environment,
    instanceId,
    data: entityRootData,
    options: options?.update,
    operation: 'unarchive',
  });

  /**
   * on successful get, refresh the entityData
   */
  useEffect(() => {
    if (get.data && onEntityGetSuccess) {
      // By cloning the data from the GET call, it removes changes directly to the api response due to prop reference, keeping the response unchanged and pure.
      const isolatedData = cloneDeep(get?.data);
      onEntityGetSuccess(isolatedData, get.included, get.meta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get.data, get.included]);

  // The set of operations exposed here is the full possible set. Each individual operation
  // gates itself via its own `enableAction` check (which falls through to the entity's filtered
  // endpoints map / `hasEntityOperation`), so consumers can rely on those flags to decide
  // whether to render UI for each operation.
  return {
    get,
    update,
    create,
    publish,
    republish,
    unpublish,
    archive,
    unarchive,
    clone,
    deepClone,
    validate,
    custom,
    bulkActionReportingStore,
    bulkActionReportingSession,
    clearBulkActionReportingSession,
  };
};
